import React, { useEffect, useState } from 'react';
import { FaDownload, FaUpload } from 'react-icons/fa';
import Spinner from './spinner';
import { months } from '../util/common';
import ApplicationService from '../services/axios.service';
import { Button } from 'antd';
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Editable flags for each field
const editFlags = {
    total: true,
    databaseLive: false,
    balanceToBill: true,
    risk: true,
    finadjustment: true,
    potential: true,
    deltavsaugbudegt: true,
    deltavsfc5: true,
    gapreason: true,
    reasonforsalesdelta: true,
};

const SalesLap = () => {
    const [accordionData, setAccordionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [period, setPeriod] = useState(1);
    const [availableYears, setAvailableYears] = useState([]);
    // State for row-level editing (keys will be "customerIndex-rowId")
    const [editingRows, setEditingRows] = useState({});
    // Store original row data for revert on cancel
    const [originalRows, setOriginalRows] = useState({});
    const [reasons, setReasons] = useState([]);
    const [isValid, setIsValid] = useState(true); // To track the validation state
    const [validationMessage, setValidationMessage] = useState(""); // Message to show error
    const [selectedMonth, setSelectedMonth] = useState(''); // State for selected month
    const navigate = useNavigate();
    const service = new ApplicationService();



    const fetchYears = async () => {
        try {
            const response = await service.getSaleslabYear();
            const result = response.data;
            if (result.statusCode === 200) {
                const years = result.data;
                setAvailableYears(years);
                setYear(years.length > 0 ? years[0] : new Date().getFullYear());
            } else {
                console.error('Error fetching years:', result.message);
            }
        } catch (error) {
            console.error('Error fetching years:', error);
        }
    };

    const fetchData = async () => {
        const payload = {
            year: year,
            month: month,
            period: period,
        }
        setLoading(true);
        try {
            const response = await service.getSalesLab(payload);
            const result = response.data;
            if (result.statusCode === 200) {
                const modifiedData = result.data.map((customer, customerIndex) => ({
                    ...customer,
                    tableData: customer.tableData.map((row, rowIndex) => ({
                        ...row,
                        id: row.id || `${customerIndex}-${rowIndex}`,
                        customer_id: customer.customerId,
                        customer: customer.customerName,
                    })),
                }));
                setAccordionData(modifiedData);
                setError(null);
            } else {
                console.error("Error fetching data:", result.message);
                setError(result.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        service.getReasons().then(response => {
            setReasons(response.data);
        }).catch(error => {
            console.error("Error fetching reasons:", error);
        });
        setSelectedMonth(months.find(m => m.value == month)?.label); // Set the selected month label based on the current month
    }, []);




    const handlePeriodChange = (e) => {
        const value = e.target.value;
        let newPeriod;

        if (value.startsWith("[") && value.endsWith("]")) {
            // If the value is an array-like string (e.g., "[4,5,6]"), convert it to an array
            newPeriod = JSON.parse(value); // Converts string to actual array [4,5,6]
            setMonth('');
        } else {
            // Otherwise, it's a single number, so we store it as an integer
            newPeriod = parseInt(value);
        }

        setPeriod(newPeriod);

        // Validation logic
        if ([1, 2, 3, 4, 5].includes(newPeriod)) {
            // Period is 1-5 weeks, so year and month must be selected
            if (!year || !month) {
                setIsValid(false);
                setValidationMessage("Please select both Year and Month for 1-5 weeks period.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        } else {
            // For other periods (Quarterly, Half yearly), only year is required
            if (!year) {
                setIsValid(false);
                setValidationMessage("Please select Year for Quarterly or Half Yearly periods.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        }
    };



    const exportData = async () => {
        const payload = {
            year: year,
            month: month,
            period: period,
        }
        setLoading(true);
        try {
            const response = await service.exportSalesLab(payload);
            const result = response.data;
            if (response.status === 200 && result) {
                const fileName = "saleslap_report.xlsx";  // Choose a name for the file

                // Create a Blob from the response
                const blob = new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = fileName;

                // Trigger the download
                link.click();
            } else {
                console.error("Error downloading data:", result.message);
                setError(result.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to form a unique key for each row
    const getRowKey = (customerIndex, rowId) => `${customerIndex}-${rowId}`;

    // When Edit is clicked for a row, store the original data and mark that row as editing
    const handleRowEditClick = (customerIndex, rowId, rowData) => {
        const key = getRowKey(customerIndex, rowId);
        setOriginalRows(prev => ({ ...prev, [key]: rowData }));
        setEditingRows(prev => ({ ...prev, [key]: true }));
    };

    // On Save, you can add your API save logic here. Then remove the row from editing mode.
    const handleRowSaveClick = async (customerIndex, rowId) => {
        const key = getRowKey(customerIndex, rowId);
        // Find the updated row data that you want to save
        const customer = accordionData[customerIndex];
        const rowData = customer.tableData.find((row) => row.id === rowId);
        console.log("Row data to save:", rowData);
        const payload = {
            modelid: rowData.customer_id,        // Replace with your actual model id
            optionid: rowData.criteria_id,       // Replace with your actual option id
            date: rowData.date,  // This will be the current date in ISO format
            gps_reason: rowData.gapreason,  // Replace with your actual reason, e.g., "Sales Adjustment"
            balance_to_bill: rowData.balanceToBill,
            risk: rowData.risk,
            fin_adjustment: rowData.finadjustment,
            potential: rowData.potential,
            year: year,
            month: month,
            period: period
        };

        try {
            // Call the API to save the row data.
            // Replace `saveRowData` with your actual API method and adjust parameters as needed.
            const response = await service.updateSalesLap(payload);

            if (response.data.statusCode === 200) {
                // API call was successful; you can add any success notification here if needed.
                console.log("Row updated successfully");
                toast.success("Row updated successfully!");
            } else {
                toast.error("Failed to update row.");
                // Handle any error responses here.
                console.error("Error saving row data:", response.data.message);
            }
        } catch (error) {
            console.error("Error saving row data:", error);
            toast.error("Failed to update row.");
        } finally {
            // Remove the row from edit mode
            setEditingRows((prev) => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
            setOriginalRows((prev) => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
        }
    };


    // When Cancel is clicked, revert the changes using stored original data and exit edit mode.
    const handleRowCancelClick = (customerIndex, rowId) => {
        const key = getRowKey(customerIndex, rowId);
        const originalData = originalRows[key];
        if (originalData) {
            setAccordionData(prevData =>
                prevData.map((customer, cIdx) => {
                    if (cIdx !== customerIndex) return customer;
                    const updatedTableData = customer.tableData.map(row => {
                        if (row.id === rowId) {
                            return { ...originalData };
                        }
                        return row;
                    });
                    return { ...customer, tableData: updatedTableData };
                })
            );
        }
        setEditingRows(prev => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
        });
        setOriginalRows(prev => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
        });
    };

    // Update a specific cell's value in the table data
    const handleInputChange = (customerIndex, rowId, field, value) => {
        setAccordionData(prevData =>
            prevData.map((customer, idx) => {
                if (idx !== customerIndex) return customer;
                const updatedTableData = customer.tableData.map(row => {
                    if (row.id === rowId) {
                        return { ...row, [field]: value };
                    }
                    return row;
                });
                return { ...customer, tableData: updatedTableData };
            })
        );
    };

    const handleMonthChange = (e) => {
        const newMonth = e.target.value;
        setMonth(newMonth); // Update the month state
        handleFilterChange(newMonth, year); // Check the validity after the month is updated
    };

    const handleYearChange = (e) => {
        const newYear = e.target.value;
        setYear(newYear); // Update the year state
        handleFilterChange(month, newYear); // Check the validity after the year is updated
    };

    const handleFilterChange = (newMonth, newYear) => {
        // Validate the fields based on the selected period
        if ([1, 2, 3, 4, 5].includes(period)) {
            // Period is 1-5 weeks, so year and month must be selected
            if (!newYear || !newMonth) {
                setIsValid(false);
                setValidationMessage("Please select both Year and Month for 1-5 weeks period.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        } else {
            // For other periods (Quarterly, Half yearly), only year is required
            if (!newYear) {
                setIsValid(false);
                setValidationMessage("Please select Year for Quarterly or Half Yearly periods.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        }
    };


    // Cancel editing for all rows in a given customer (accordion) index
    const cancelEditingForCustomer = (customerIndex) => {
        Object.keys(editingRows)
            .filter((key) => key.startsWith(`${customerIndex}-`))
            .forEach((key) => {
                // Extract the rowId by removing the customerIndex and the dash from the key.
                const rowId = key.substring(String(customerIndex).length + 1);
                handleRowCancelClick(customerIndex, rowId);
            });
    };

    // Toggle the accordion for a given index and cancel editing in the previously opened accordion
    const handleAccordionToggle = (index) => {
        if (openIndex !== null && openIndex !== index) {
            cancelEditingForCustomer(openIndex);
        }
        setOpenIndex(openIndex === index ? null : index);
    };


    const handleSearch = () => {
        setSelectedMonth(months.find(m => m.value == month)?.label); // Set the selected month label based on the current month
        if ([1, 2, 3, 4, 5].includes(period)) {
            // Period is 1-5 weeks, so year and month must be selected
            if (!year || !month) {
                setIsValid(false);
                setValidationMessage("Please select both Year and Month for 1-5 weeks period.");
                return; // Prevent search from executing
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
                // Proceed with the search operation here
                fetchData(); // Call your fetchData function here
            }
        } else {
            // For other periods (Quarterly, Half yearly), only year is required
            if (!year) {
                setIsValid(false);
                setValidationMessage("Please select Year for Quarterly or Half Yearly periods.");
                return; // Prevent search from executing
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
                // Proceed with the search operation here
                fetchData(); // Call your fetchData function here
            }
        }
    };

    // Render accordion layout using the dynamic API data
    const renderAccordionLayout = () => (
        <div className="max-h-[410px] overflow-auto">
            <ul className="space-y-2">
                {accordionData.map((customer, customerIndex) => (
                    <li key={customer.customerName}>
                        {openIndex !== customerIndex && (
                            <button
                                type="button"
                                className={`flex items-center justify-between w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none cursor-pointer focus:ring-2 focus:ring-gray-300 hover:bg-gray-50 ${openIndex === customerIndex ? 'bg-gray-100' : 'bg-white'}`}
                                onClick={() => handleAccordionToggle(customerIndex)}
                            >
                                <span className="font-medium text-gray-700">
                                    {customer.customerName}
                                </span>
                                <div className="flex items-center space-x-6">
                                    <span className="font-bold">Total - ${customer.total}</span>
                                    <svg
                                        className={`w-4 h-4 transition-transform ${openIndex === customerIndex ? 'rotate-90' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </div>
                            </button>
                        )}
                        {openIndex === customerIndex && (
                            <div className="overflow-x-auto">
                                <table className="min-w-max w-full text-sm border-y border-[#E6E6E6]">
                                    <thead className="bg-[#F5F7FF] text-[#000000] border-y border-[#E6E6E6] h-[40px]">
                                        <tr>
                                            <th className="px-4 py-2 font-semibold">Customer</th>
                                            <th className="px-4 py-2 font-semibold">Criteria</th>
                                            <th className="px-4 py-2 font-semibold">FCS</th>
                                            <th className="px-4 py-2 font-semibold">{selectedMonth} Budget</th>
                                            <th className="px-4 py-2 font-semibold">{selectedMonth} FCA</th>
                                            {/* <th /> */}
                                            <th className="px-4 py-2 font-semibold">Total</th>
                                            <th className="px-4 py-2 font-semibold">Database Live</th>
                                            <th className="px-4 py-2 font-semibold">Balance to Bill</th>
                                            <th className="px-4 py-2 font-semibold">Risk</th>
                                            <th className="px-4 py-2 font-semibold">Fin Adjustment</th>
                                            <th className="px-4 py-2 font-semibold">Potential</th>
                                            <th className="px-4 py-2 font-semibold">Delta vs {selectedMonth} Budget</th>
                                            <th className="px-4 py-2 font-semibold">Delta vs Fc5</th>
                                            <th className="px-4 py-2 font-semibold">Gap Reason</th>
                                            {[1, 2, 3, 4, 5].includes(period) && <th className="px-4 py-2 font-semibold">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customer.tableData.map((row, rowIndex) => {
                                            const rowKey = getRowKey(customerIndex, row.id);
                                            const isEditing = editingRows[rowKey] || false;
                                            return (
                                                <tr
                                                    key={row.id}
                                                    className="border-b border-[#E6E6E6] hover:bg-[#F5F7FF] h-[40px]"
                                                >
                                                    {rowIndex === 0 ? (
                                                        <td
                                                            onClick={() => handleAccordionToggle(customerIndex)}
                                                            className="px-4 py-2 cursor-pointer text-[#E61E1E] font-bold text-[12px]"
                                                        >
                                                            {customer.customerName}
                                                        </td>
                                                    ) : (
                                                        <td />
                                                    )}
                                                    <td className="px-4 py-2">{row.criteria}</td>
                                                    <td className="px-4 py-2">{row.fcs}</td>
                                                    <td className="px-4 py-2">{row.avgBudget}</td>
                                                    <td className="px-4 py-2">{row.avgFca}</td>
                                                    {/* {rowIndex === 0 && (
                                                    <td
                                                        rowSpan={customer.tableData.length}
                                                        className="px-3 py-2 text-center text-black bg-[#FFE9E9] border-b"
                                                        style={{ writingMode: 'sideways-lr' }}
                                                    >
                                                        {period === "1 - Week" ||
                                                            period === "2 - Weeks" ||
                                                            period === "3 - Weeks" ||
                                                            period === "4 - Weeks" ? `${month} ${year} - ${period}` : ''}
                                                        {period === "Monthly" && `${month}  ${year}`}
                                                    </td>
                                                )} */}
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.total ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.total}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'total', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.total
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.databaseLive ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.databaseLive}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'databaseLive', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.databaseLive
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.balanceToBill ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.balanceToBill}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'balanceToBill', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.balanceToBill
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.risk ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.risk}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'risk', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.risk
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.finadjustment ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.finadjustment}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'finadjustment', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.finadjustment
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.potential ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.potential}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'potential', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.potential
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.deltavsaugbudegt ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.deltavsaugbudegt}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'deltavsaugbudegt', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.deltavsaugbudegt
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.deltavsfc5 ? (
                                                            <div className="relative">
                                                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">
                                                                    $
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    value={row.deltavsfc5}
                                                                    onChange={(e) =>
                                                                        handleInputChange(customerIndex, row.id, 'deltavsfc5', e.target.value)
                                                                    }
                                                                    className="w-full border border-gray-300 pl-5 rounded-md p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                />
                                                            </div>
                                                        ) : (
                                                            row.deltavsfc5
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing && editFlags.gapreason ? (
                                                            <select
                                                                id="customSelect"
                                                                name="customSelect"
                                                                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 pr-8 rounded focus:outline-none"
                                                                value={row.gapreason}
                                                                onChange={(e) =>
                                                                    handleInputChange(customerIndex, row.id, 'gapreason', e.target.value)
                                                                }
                                                            >
                                                                {reasons.map((reason) => (
                                                                    <option key={reason.id} value={reason.name}>
                                                                        {reason.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            row.gapreason
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        {isEditing ? (
                                                            <>
                                                                <Button
                                                                    onClick={() => handleRowSaveClick(customerIndex, row.id)}
                                                                    style={{ marginRight: 8 }}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button onClick={() => handleRowCancelClick(customerIndex, row.id)}>Cancel</Button>
                                                            </>
                                                        ) : (
                                                            [1, 2, 3, 4, 5].includes(period) && (
                                                                <EditOutlined
                                                                    onClick={() => handleRowEditClick(customerIndex, row.id, row)}
                                                                    style={{ marginRight: 8, cursor: 'pointer' }}
                                                                />
                                                            )
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="bg-gray-50 font-semibold">
                                            {
                                                Object.entries(customer.subtotal).map(([key, value]) => (<>
                                                    <td className="px-4 py-2" key={key}>{value}</td>
                                                </>
                                                ))
                                            }
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );

    useEffect(() => {
        fetchYears();
        fetchData();
    }, []);

    return (
        <div className="p-4 bg-white w-full mb-4 shadow-md rounded-md">
            {/* Filter Row */}
            <div className="p-4">
                <div className="flex gap-x-5 mb-4">
                    <div className="md:w-1/4 w-full">
                        <label htmlFor="year" className="block text-sm font-semibold text-gray-700 mb-1">
                            Year
                        </label>
                        <select id="year" className="border border-gray-300 rounded px-2 py-1 w-full h-10 cursor-pointer" value={year} onChange={handleYearChange}>
                            <option value="">Select Year</option>
                            {availableYears.map((yr, idx) => (
                                <option key={idx} value={yr}>
                                    {yr}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:w-1/4 w-full">
                        <label htmlFor="month" className="block text-sm font-semibold text-gray-700 mb-1">
                            Month
                        </label>
                        <select
                            id="month"
                            value={month}
                            onChange={handleMonthChange}
                            className="w-full border border-gray-300 rounded px-2 py-1 h-10 cursor-pointer"
                        >
                            <option value="">Select Month</option>
                            {months.map((m, index) => (
                                <option key={index} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:w-1/4 w-full">
                        <label htmlFor="period" className="block text-sm font-semibold text-gray-700 mb-1">
                            Period
                        </label>
                        <select
                            id="period"
                            className="border border-gray-300 rounded px-2 py-1 w-full h-10 cursor-pointer"
                            // value={period}
                            value={Array.isArray(period) ? JSON.stringify(period) : period} // Convert array to string for comparison
                            onChange={handlePeriodChange}
                        >
                            <option value="">Select Period</option>
                            <option value="1">1 - Week</option>
                            <option value="2">2 - Weeks</option>
                            <option value="3">3 - Weeks</option>
                            <option value="4">4 - Weeks</option>
                            <option value="5">5 - Weeks</option>
                            <option value="[4,5,6]">Quarterly 1</option>
                            <option value="[7,8,9]">Quarterly 2</option>
                            <option value="[10,11,12]">Quarterly 3</option>
                            <option value="[1,2,3]">Quarterly 4</option>
                            <option value="[4,5,6,7,8,9]">Half yearly 1</option>
                            <option value="[10,11,12,1,2,3]">Half yearly 2</option>
                        </select>
                    </div>
                    <div className="md:w-1/4 w-full">
                        <label className="block text-sm text-gray-600 mb-1 invisible">Search</label>
                        <button
                            className={`bg-[#3D3D3D] w-full h-10 rounded-md ${isValid ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            onClick={handleSearch} disabled={!isValid}>
                            <span className="text-white">Search</span>
                        </button>
                    </div>
                </div>
                {!isValid && (
                    <div className="text-red-500 mt-2">{validationMessage}</div>
                )}
            </div>
            {/* Main Content */}
            <div className="flex">
                <h3 className="text-lg font-semibold text-[#1D1D1D] mb-4">Customers</h3>
                <div className="ml-auto flex gap-x-5 py-2">
                    <button className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={() => navigate("/upload-saleslap-excel")}>
                        <FaUpload className="text-white" />
                        <span className="text-white">Import</span>
                    </button>
                    <button className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={exportData}>
                        <FaDownload className="text-white" />
                        <span className="text-white">Export</span>
                    </button>
                </div>
            </div>
            {loading ? (
                <div>
                    <Spinner />
                </div>
            ) : error ? (
                <div>Error: {error}</div>
            ) : (
                renderAccordionLayout()
            )}
        </div>
    );
};

export default SalesLap;
